import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TableSortLabel,
  TextField,
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminConfigTractorHistory.module.css';

const columns = [
  { id: 'tableName', label: 'Table Name' },
  { id: 'operation', label: 'Operation' },
  { id: 'field', label: 'Field' },
  { id: 'oldValue', label: 'Old Value' },
  { id: 'newValue', label: 'New Value' },
  { id: 'rowLoadedUtc', label: 'Change Date' },
];

function descendingComparator(a, b, orderBy) {
  if (orderBy === 'rowLoadedUtc') {
    return new Date(b[orderBy]) - new Date(a[orderBy]);
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const AdminConfigTractorHistory = () => {
  const [searchParams] = useSearchParams();
  const [auditChanges, setAuditChanges] = useState([]);
  const [tractor, setTractor] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('rowLoadedUtc');
  const [filters, setFilters] = useState({
    tableName: '',
    operation: '',
    field: '',
    oldValue: '',
    newValue: '',
    rowLoadedUtc: '',
  });

  const tractorId = searchParams.get('tractorId');
  const clientId = searchParams.get('clientId');

  useEffect(() => {
    const fetchTractor = async () => {
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/tractor?clientId=${clientId}&tractorId=${tractorId}`,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : {},
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch tractor: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setTractor(data);
      } catch (err) {
        setTractor({});
      }
    };

    if (tractorId && clientId) {
      fetchTractor();
    }
  }, [tractorId, clientId]);

  useEffect(() => {
    const fetchAuditChanges = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/auditlogs/tractor?tractorId=${tractorId}`,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : {},
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setAuditChanges(data);
      } catch (err) {
        setError(err.message || 'Failed to load audit log');
      } finally {
        setLoading(false);
      }
    };

    if (tractorId) {
      fetchAuditChanges();
    }
  }, [tractorId]);

  const formatDate = (utcString) => {
    if (!utcString) return '';
    const date = new Date(utcString);
    return date.toLocaleString();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (columnId, value) => {
    setFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  // Filtering logic: all filters are case-insensitive substring matches
  const filteredRows = React.useMemo(() => {
    return auditChanges.filter((row) =>
      columns.every((col) => {
        const filterValue = filters[col.id];
        if (!filterValue) return true;
        let cellValue = row[col.id];
        if (col.id === 'rowLoadedUtc') {
          cellValue = formatDate(cellValue);
        }
        return (
          cellValue &&
          cellValue
            .toString()
            .toLowerCase()
            .includes(filterValue.toString().toLowerCase())
        );
      })
    );
  }, [auditChanges, filters]);

  const sortedRows = React.useMemo(
    () => filteredRows.slice().sort(getComparator(order, orderBy)),
    [filteredRows, order, orderBy]
  );

  return (
    <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
      <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
        {tractor.name}
      </Typography>
      <Typography variant="h5" sx={{ pb: 2 }}>
        Boom Management
      </Typography>
      <Typography variant="h4" sx={{ pt: 2, pb: 2 }}>
        Tractor Audit History
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            my: 2,
            maxHeight: 750,
            overflow: 'auto',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    sortDirection={orderBy === col.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <TextField
                      size="small"
                      variant="standard"
                      value={filters[col.id]}
                      onChange={(e) => handleFilterChange(col.id, e.target.value)}
                      placeholder={`Filter ${col.label}`}
                      fullWidth
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No audit history found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((change, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{change.tableName}</TableCell>
                    <TableCell>{change.operation}</TableCell>
                    <TableCell>{change.field}</TableCell>
                    <TableCell>{change.oldValue}</TableCell>
                    <TableCell>{change.newValue}</TableCell>
                    <TableCell>{formatDate(change.rowLoadedUtc)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminConfigTractorHistory;